import { findIndustryCodeDeep, industryCodes } from "@/data/industry-codes";
import { formatRelativeDate, rangeFilterToForm } from "@/utils/filter-utils";
import { formatNumber } from "@/utils/number-utils";
import {
  ComparisonOperator,
  comparisonOperators,
  textOperators,
} from "@/utils/operators";
import { formatListValues } from "@/utils/sidebar";
import { pluralize } from "@/utils/string-utils";
import { isOneOf } from "@/utils/type-guards";
import { Landmark } from "lucide-react";
import { companyStatuses, companyTypes } from "../../company";
import { FilterCategory, getFilterByFilterName } from "../config";
import { createFilterGroup, filterGroupTypes } from "../filter-types";

export const basicFilterCategory = {
  id: "basic",
  title: "Stamoplysninger",
  icon: Landmark,
  description: "Grundlæggende virksomhedsoplysninger",
  filters: {
    industry: createFilterGroup({
      id: "industry",
      filterType: filterGroupTypes.industry,
      filters: {
        value: { fieldName: "industrycode", filterName: "basic-industry" },
      },
      title: "Branche",
      fullWidth: true,
      keywords: ["branchekode", "industri", "erhverv"],
      DisplayValue: ({ filters }) => {
        const industryFilter = getFilterByFilterName("basic-industry", filters);
        if (!industryFilter?.values) return "Ikke angivet";

        const industryCodesWithTitle = industryFilter.values.map(
          (value, index) => ({
            id: value,
            // Since we only ever show the first two industry codes in the
            // sidebar, we should only get the industry name for those.
            name:
              index < 2 ? findIndustryCodeDeep(industryCodes, value)?.name : "",
          }),
        );

        const operatorText =
          comparisonOperators.values[
            industryFilter.operator as ComparisonOperator
          ] ?? "Lig med";

        return `${operatorText} ${formatListValues(
          industryCodesWithTitle.map((code) => code.name ?? ""),
        )} ${industryFilter.fieldNames?.length ? "(inkl. bibrancher)" : ""}`;
      },
    }),
    employees: createFilterGroup({
      id: "employees",
      filterType: filterGroupTypes.employees,
      filters: {
        value: { fieldName: "employees", filterName: "basic-employees-value" },
        change: {
          fieldName: "BasicInfo.EmployeeCountChange12Month",
          filterName: "basic-employees-change",
        },
      },
      title: "Antal ansatte",
      keywords: ["medarbejdere", "vækst", "størrelse"],
      DisplayValue: ({ filters }) => {
        const employeeCountFilter = getFilterByFilterName(
          "basic-employees-value",
          filters,
        );

        const employeesChangeFilter = getFilterByFilterName(
          "basic-employees-change",
          filters,
        );

        let baseDescription = "Ikke angivet";

        if (
          !isOneOf(
            employeeCountFilter?.operator,
            "EventFilter",
            "DepartmentSize",
          ) &&
          employeeCountFilter?.values
        ) {
          const { min, max } = rangeFilterToForm(employeeCountFilter);

          if (employeeCountFilter.operator === "Equal" && min === max) {
            baseDescription = `${min}`;
          } else {
            if (min && max) {
              baseDescription = `${min}-${max}`;
            } else if (min) {
              baseDescription = `Over ${min}`;
            } else if (max) {
              baseDescription = `Under ${max}`;
            }
          }
        }

        const growthPercentage = employeesChangeFilter?.values?.[0];

        if (growthPercentage) {
          return baseDescription !== "Ikke angivet"
            ? `${baseDescription}, ${growthPercentage}% i vækst`
            : `${growthPercentage}% i vækst`;
        }

        return baseDescription;
      },
    }),
    departmentSize: createFilterGroup({
      id: "department-size",
      isAdvancedFilter: true,
      filterType: filterGroupTypes.departmentSize,
      filters: {
        value: {
          fieldName: "DepartmentSize",
          filterName: "basic-department-size",
        },
      },
      title: "Afdelinger",
      keywords: ["ansatte", "personer", "roller"],
      description:
        "Find firmaer med bestemte afdelinger. Dette vurderes ud fra hvor mange kontaktpersoner der kan findes i en given afdeling.",
      DisplayValue: ({ filters }) => {
        const departmentSizeFilter = getFilterByFilterName(
          "basic-department-size",
          filters,
        );
        if (departmentSizeFilter?.operator === "DepartmentSize") {
          const groups = departmentSizeFilter.groups;
          if (!groups || groups.length === 0) return "Ikke angivet";

          const descriptions = groups.map((group) => {
            const values = group.values;
            const department = values?.[0];
            const minSize = values?.[1];
            const maxSize = values?.[2];
            if (group.operator === "GreaterEqual" && minSize !== undefined) {
              return `Mindst ${minSize} ${pluralize(parseInt(minSize, 10), "kontaktperson", "kontaktpersoner")} i ${department}`;
            } else if (
              group.operator === "LessEqual" &&
              minSize !== undefined
            ) {
              return `Højst ${minSize} ${pluralize(parseInt(minSize, 10), "kontaktperson", "kontaktpersoner")} i ${department}`;
            } else if (
              group.operator === "Between" &&
              minSize !== undefined &&
              maxSize !== undefined
            ) {
              return `Mellem ${minSize}-${values?.[2]} ${pluralize(parseInt(maxSize, 10), "kontaktperson", "kontaktpersoner")} i ${department}`;
            } else if (group.operator === "Equal" && minSize !== undefined) {
              return `Præcis ${minSize} ${pluralize(parseInt(minSize, 10), "kontaktperson", "kontaktpersoner")} i ${department}`;
            }
            return "";
          });

          return descriptions.join(", ");
        }

        return "Ikke angivet";
      },
    }),
    productionUnitsCount: createFilterGroup({
      type: "number",
      id: "production-units-count",
      filterType: filterGroupTypes.number,
      filters: {
        value: {
          fieldName: "BasicInfo.ProductionUnitCount",
          filterName: "basic-production-units-count",
        },
      },
      title: "Antal filialer",
      keywords: ["afdelinger", "enheder", "produktionsenheder"],
      description:
        "Antal filialer som virksomheder har registreret i CVR i form af produktionsenheder",
      DisplayValue: ({ filters }) => {
        const productionUnitsCountFilter = getFilterByFilterName(
          "basic-production-units-count",
          filters,
        );

        if (
          isOneOf(
            productionUnitsCountFilter?.operator,
            "EventFilter",
            "DepartmentSize",
          ) ||
          !productionUnitsCountFilter?.values
        )
          return "Ikke angivet";

        const { min, max } = rangeFilterToForm(productionUnitsCountFilter);

        if (
          productionUnitsCountFilter.operator === "Equal" &&
          min &&
          max &&
          min === max
        ) {
          return formatNumber(min);
        }

        if (min && max) {
          return `${formatNumber(min)}-${formatNumber(max)}`;
        } else if (min) {
          return `> ${formatNumber(min)}`;
        } else if (max) {
          return `< ${formatNumber(max)}`;
        }

        return "Ikke angivet";
      },
    }),
    creationDate: createFilterGroup({
      id: "creation-date",
      filterType: filterGroupTypes.date,
      filters: {
        value: {
          fieldName: "BasicInfo.CreationDate",
          filterName: "basic-creation-date",
        },
      },
      title: "Stiftelsesdato",
      keywords: ["etableringsdato", "grundlagt", "alder", "grundlæggelse"],
      description:
        "Vælg hvor gamle virksomhederne må være ud fra stiftelsestidspunkterne.",
      DisplayValue: ({ filters }) => {
        const basicCreationDateFilter = getFilterByFilterName(
          "basic-creation-date",
          filters,
        );

        if (!basicCreationDateFilter) return "Ikke angivet";

        const { operator, values } = basicCreationDateFilter;

        if (!values) return "Ikke angivet";

        // Handle operators
        return formatRelativeDate(values, operator);
      },
    }),
    purpose: createFilterGroup({
      id: "purpose",
      filterType: filterGroupTypes.string,
      filters: {
        value: { fieldName: "BasicInfo.Purpose", filterName: "basic-purpose" },
      },
      title: "Formålsbeskrivelse",
      keywords: [],
      description:
        'Skriv en eksakt tekst, der skal indgå i virksomhedernes formålsbeskrivelser. Det kan eksempelvis være "ESG" eller "Salg af lægemidler".',
      DisplayValue: ({ filters }) => {
        const purposeFilter = getFilterByFilterName("basic-purpose", filters);

        if (!purposeFilter?.values) return "Ikke angivet";

        const { operator, values } = purposeFilter;

        const formattedPurposes = formatListValues(values, "eller");

        return `${textOperators.map.get(operator)} ${formattedPurposes}`;
      },
    }),
    companyType: createFilterGroup({
      id: "company-type",
      filterType: filterGroupTypes.companyType,
      filters: {
        value: {
          fieldName: "BasicInfo.formCode",
          filterName: "basic-company-type",
        },
      },
      title: "Virksomhedsform",
      keywords: ["formkode", "selskabsform", "virksomhedstype", "selskabstype"],
      DisplayValue: ({ filters }) => {
        const companyTypeFilter = getFilterByFilterName(
          "basic-company-type",
          filters,
        );

        if (!companyTypeFilter) return "Ikke angivet";

        const companyTypeList = formatListValues(
          companyTypeFilter?.values?.map(
            (item) =>
              companyTypes.find((type) => type.value === Number(item))?.label ||
              "",
          ) ?? [],
          "eller",
        );
        return companyTypeFilter?.operator === "NotEqual"
          ? `Ikke lig med ${companyTypeList}`
          : `Lig med ${companyTypeList}`;
      },
    }),
    soloOwnedCompany: createFilterGroup({
      id: "solo-owned-company",
      filterType: filterGroupTypes.boolean,
      filters: {
        value: {
          fieldName: "BasicInfo.IsSingularlyOwned",
          filterName: "basic-solo-owned-company",
        },
      },
      title: "Solo-ejet virksomhed",
      keywords: ["eneejet", "ejerstruktur"],
      description: "Virksomheden har én reel ejer, der ejer hele virksomheden.",
      trueLabel: "Skal være solo-ejet",
      falseLabel: "Må ikke være solo-ejet",
      DisplayValue: ({ filters }) => {
        const soloOwnedCompanyFilter = getFilterByFilterName(
          "basic-solo-owned-company",
          filters,
        );

        if (!soloOwnedCompanyFilter?.values) return "Ikke angivet";

        return soloOwnedCompanyFilter.values.includes("true") ? "Ja" : "Nej";
      },
    }),
    excludeHolding: createFilterGroup({
      id: "exclude-holding",
      filterType: filterGroupTypes.boolean,
      filters: {
        value: {
          fieldName: "BasicInfo.IsHolding",
          filterName: "basic-exclude-holding",
        },
      },
      title: "Er holdingselskab",
      keywords: [],
      description:
        "Vælg om holdingselskaber skal ekskluderes fra din målgruppe. Holdingselskaber identificeres ved deres branchekode og/eller formålsbeskrivelse.",
      trueLabel: "Skal være holdingselskab",
      falseLabel: "Må ikke være holdingselskab",
      DisplayValue: ({ filters }) => {
        const includeHoldingFilter = getFilterByFilterName(
          "basic-exclude-holding",
          filters,
        );

        if (!includeHoldingFilter?.values) return "Ikke angivet";

        return includeHoldingFilter.values.includes("true") ? "Ja" : "Nej";
      },
    }),
    companyStatus: createFilterGroup({
      id: "company-status",
      filterType: filterGroupTypes.companyStatus,
      filters: {
        value: {
          fieldName: "BasicInfo.CompanyStatus",
          filterName: "basic-company-status",
        },
      },
      title: "Virksomhedsstatus",
      keywords: [],
      DisplayValue: ({ filters }) => {
        const companyStatusFilter = getFilterByFilterName(
          "basic-company-status",
          filters,
        );
        if (
          isOneOf(
            companyStatusFilter?.operator,
            "EventFilter",
            "DepartmentSize",
          ) ||
          !companyStatusFilter?.values
        )
          return "Ikke angivet";
        const { operator, values } = companyStatusFilter;
        const statusNames = formatListValues(
          values
            .filter((status) => status !== "NORMAL")
            .map(
              (status) =>
                companyStatuses.values[
                  status as keyof typeof companyStatuses.values
                ],
            ),
          "eller",
        );

        return operator === "NotEqual"
          ? `Ikke lig med ${statusNames}`
          : `Lig med ${statusNames}`;
      },
    }),
    familyDriven: createFilterGroup({
      id: "family-driven",
      filterType: filterGroupTypes.boolean,
      filters: {
        value: {
          fieldName: "BasicInfo.IsFamilyDriven",
          filterName: "basic-family-driven",
        },
      },
      title: "Familiedrevet virksomhed",
      keywords: ["familieejet"],
      description:
        "Virksomheden har to eller flere direktions- og bestyrelsesmedlemmer, med samme efternavn",
      trueLabel: "Skal være familiedrevet",
      falseLabel: "Må ikke være familiedrevet",
      DisplayValue: ({ filters }) => {
        const familyDrivenFilter = getFilterByFilterName(
          "basic-family-driven",
          filters,
        );

        if (!familyDrivenFilter?.values) return "Ikke angivet";

        return familyDrivenFilter.values.includes("true") ? "Ja" : "Nej";
      },
    }),
  },
} satisfies FilterCategory;
